import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import type { CustomFieldInputType } from "@/api/db/connection";
import type { TermDetail } from "@/api/db/terms";
import type { DocumentBlock, DocumentPage } from "@/api/document/block";
import { FloatingPlacement } from "@/api/document/floats";
import { A4_METRICS } from "@/api/document/paginate";
import { documentStyles } from "@/api/document/styles";
import { ManualRules } from "@/api/manual/rules";
import { orpc } from "@/client";
import { DocumentEditor, useDocumentEditor } from "@/components/document-editor/editor";
import type { EditorField, EditorPattern } from "@/components/document-editor/fields-context";
import type { CreateFieldContext } from "@/components/document-editor/fields-suggestion";
import { FloatingLayer } from "@/components/document-editor/floating-layer";
import { ImageActionsContext } from "@/components/document-editor/image-actions";
import { setLintFlags } from "@/components/document-editor/lint";
import { PAGE_STRIDE } from "@/components/document-editor/pagination";
import { DocumentPatternSelection } from "@/components/document-editor/pattern-selection";
import { DocumentToolbar } from "@/components/document-editor/toolbar";
import { TerminologyWarnings } from "@/components/document-editor/warnings";
import { SkeletonRows } from "@/components/ui/skeleton";
import { useTermDetail, useTermImageUpload } from "@/hooks/use-terms";
import { BrowserFile } from "@/lib/browser-file";
import { errorCode } from "@/lib/error-code";
import { TermosLabels } from "@/lib/labels/termos";
import { kebab } from "@/lib/slug";

import { FieldDialog } from "./field-dialog";
import { PatternDialog } from "./pattern-dialog";
import { ReviewBanner } from "./review-banner";
import { TermChrome } from "./term-chrome";
import { useTermFloats } from "./use-term-floats";

export function TermDocument({ id }: { id: number }) {
	const detail = useTermDetail(id);
	const globals = useQuery(orpc.documents.fields.list.queryOptions());

	if (detail.isError) {
		return <TermError message={TermosLabels.error(errorCode(detail.error))} />;
	}

	if (detail.data === undefined || globals.data === undefined) {
		return (
			<div className="mx-auto w-full max-w-180 px-4 py-8">
				<SkeletonRows rows={6} />
			</div>
		);
	}

	if (detail.data.pages === null) {
		return <TermError message={TermosLabels.error("NOT_MIGRATED")} />;
	}

	const fields: EditorField[] = [
		...globals.data.map((field) => ({ id: field.id, label: field.label, value: null })),
		...detail.data.customFields.map((field) => ({
			id: field.id,
			label: field.label,
			value: null,
			inputType: field.input_type,
			patternId: field.pattern_id,
			source: field.source,
		})),
	];
	const patterns: EditorPattern[] = detail.data.patterns.map((pattern) => ({
		id: pattern.id,
		name: pattern.name,
		label: pattern.label,
		minimumItems: pattern.minimum_items,
	}));

	return (
		<TermEditor
			key={detail.data.versionId}
			term={detail.data}
			initialPages={detail.data.pages}
			initialFields={fields}
			initialPatterns={patterns}
		/>
	);
}

function TermError({ message }: { message: string }) {
	return (
		<div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 bg-background px-4 text-center">
			<p className="text-base text-foreground/70">{message}</p>

			<Link
				to="/clinica"
				className="inline-flex items-center gap-2 rounded-md border border-foreground/15 px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
			>
				<ArrowLeft className="size-4" />
				{TermosLabels.editor.back}
			</Link>
		</div>
	);
}

const IMAGE_UPLOAD_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

function TermEditor({
	term,
	initialPages,
	initialFields,
	initialPatterns,
}: {
	term: TermDetail;
	initialPages: DocumentPage[];
	initialFields: EditorField[];
	initialPatterns: EditorPattern[];
}) {
	const [name, setName] = useState(term.name);
	const [blocksDirty, setBlocksDirty] = useState(false);
	const [warnings, setWarnings] = useState(() => ManualRules.lintBlocks(term.blocks));
	const [fields, setFields] = useState(initialFields);
	const [patterns, setPatterns] = useState(initialPatterns);
	const [patternDraft, setPatternDraft] = useState<
		| {
				mode: "create";
				range: NonNullable<ReturnType<typeof DocumentPatternSelection.range>>;
		  }
		| { mode: "edit"; pattern: EditorPattern }
		| null
	>(null);
	const [draft, setDraft] = useState<
		| { mode: "create"; name: string; label: string; patternId: number | null }
		| { mode: "edit"; field: EditorField & { inputType: CustomFieldInputType } }
		| null
	>(null);
	const saved = useRef({ name: term.name, blocks: term.blocks });
	const fileInput = useRef<HTMLInputElement>(null);

	const imageUpload = useTermImageUpload();

	const openNewField = useCallback((context: CreateFieldContext) => {
		const patternId = DocumentPatternSelection.id(context.editor, context.range.from);

		context.editor.chain().focus().deleteRange(context.range).run();
		const label = context.query.trim();
		setDraft({
			mode: "create",
			label,
			name: kebab(label),
			patternId,
		});
	}, []);

	const editor = useDocumentEditor({
		blocks: term.blocks,
		fields,
		paginated: true,
		margins: term.margins,
		onCreateField: openNewField,
		onPagesChange: (groups) => {
			floats.syncPages(groups);
		},
		onImageFiles: (files, pos) => {
			void insertImages(files, pos);
		},
		onUpdate: (blocks) => {
			setBlocksDirty(JSON.stringify(blocks) !== JSON.stringify(saved.current.blocks));
			setWarnings(ManualRules.lintBlocks(blocks));
			floats.reanchor(blocks);
		},
	});

	const floats = useTermFloats({ term, initialPages, editor });

	useEffect(() => {
		setLintFlags(editor, warnings);
	}, [editor, warnings]);

	const blockInsertPos = () => {
		const { $to } = editor.state.selection;

		return $to.depth === 0 ? $to.pos : $to.after(1);
	};

	const insertImages = async (files: File[], pos: number | null) => {
		for (const file of files) {
			if (!IMAGE_UPLOAD_TYPES.has(file.type)) {
				continue;
			}

			const data = await BrowserFile.toBase64(file);
			const uploaded = await imageUpload.mutateAsync({ data }).catch(() => null);

			if (uploaded === null) {
				continue;
			}

			const bitmap = await createImageBitmap(file);
			const { width, height } = FloatingPlacement.fitToFlow(term.margins, bitmap);

			bitmap.close();

			editor
				.chain()
				.focus()
				.insertContentAt(pos ?? blockInsertPos(), {
					type: "image",
					attrs: { file: uploaded.file, width, height },
				})
				.run();
		}
	};

	const pickImages = () => {
		fileInput.current?.click();
	};

	const dirty = blocksDirty || name !== saved.current.name || floats.dirty;

	const markSaved = (savedName: string, blocks: DocumentBlock[]) => {
		saved.current = { name: savedName, blocks };
		setBlocksDirty(false);
		setName(savedName);
		floats.markSaved();
	};

	const addField = (field: EditorField) => {
		setFields((current) => [...current, field]);
		editor
			.chain()
			.focus()
			.insertContent({ type: "field", attrs: { fieldId: field.id } })
			.run();
	};

	const saveField = (field: EditorField) => {
		if (draft?.mode === "edit") {
			setFields((current) =>
				current.map((candidate) => (candidate.id === field.id ? field : candidate)),
			);

			return;
		}

		addField(field);
	};

	const openPattern = () => {
		const range = DocumentPatternSelection.range(editor);

		if (range === null) {
			return;
		}

		setPatternDraft({ mode: "create", range });
	};

	const insertPattern = (pattern: EditorPattern) => {
		if (patternDraft === null) {
			return;
		}

		if (patternDraft.mode === "edit") {
			setPatterns((current) =>
				current.map((candidate) => (candidate.id === pattern.id ? pattern : candidate)),
			);

			return;
		}

		setPatterns((current) => {
			if (current.some((candidate) => candidate.id === pattern.id)) {
				return current;
			}

			return [...current, pattern];
		});
		editor
			.chain()
			.focus()
			.insertContentAt(
				{ from: patternDraft.range.from, to: patternDraft.range.to },
				{
					type: "pattern",
					attrs: { patternId: pattern.id },
					content: patternDraft.range.content,
				},
			)
			.run();
	};

	return (
		<div className="min-h-dvh bg-background">
			<TermChrome
				termId={term.id}
				name={name}
				onNameChange={setName}
				dirty={dirty}
				editor={editor}
				buildDocument={(blocks) => ({
					pages: floats.pagesForSave(blocks),
					floats: floats.floatsForSave(),
				})}
				onSaved={markSaved}
				toolbar={
					<DocumentToolbar
						editor={editor}
						fields={fields}
						kind="term"
						onInsertImage={pickImages}
						onEditField={(field) => {
							if (field.inputType !== undefined && field.source === "input") {
								setDraft({ mode: "edit", field: { ...field, inputType: field.inputType } });
							}
						}}
						onCreatePattern={openPattern}
						onEditPattern={(patternId) => {
							const pattern = patterns.find((candidate) => candidate.id === patternId);

							if (pattern !== undefined) {
								setPatternDraft({ mode: "edit", pattern });
							}
						}}
					/>
				}
			/>

			<input
				ref={fileInput}
				type="file"
				accept="image/png,image/jpeg,image/webp"
				multiple
				hidden
				onChange={(event) => {
					void insertImages([...(event.target.files ?? [])], null);
					event.target.value = "";
				}}
			/>

			{(term.incomplete ||
				term.reviewRequired ||
				term.issues.some((issue) => issue.resolution === null)) && <ReviewBanner term={term} />}

			<ImageActionsContext.Provider value={floats.imageActions}>
				<DocumentEditor
					editor={editor}
					fields={fields}
					patterns={patterns}
					kind="term"
					template
					margins={term.margins}
					typography={term.typography}
					stylesheet={documentStyles.term}
					header={
						<FloatingLayer
							floats={floats.floats}
							pages={floats.pages}
							stride={PAGE_STRIDE}
							pageWidth={A4_METRICS.pageWidth}
							pageHeight={A4_METRICS.pageHeight}
							selection={floats.selection}
							onSelect={floats.setSelection}
							onCommit={floats.onCommit}
							onToFlow={floats.onToFlow}
							onRemove={floats.onRemove}
						/>
					}
				/>
			</ImageActionsContext.Provider>

			{warnings.length > 0 && (
				<div className="mx-auto max-w-170 px-4 pb-8">
					<TerminologyWarnings warnings={warnings} />
				</div>
			)}

			{draft !== null && (
				<FieldDialog
					termId={term.id}
					draft={draft}
					onClose={() => setDraft(null)}
					onSaved={saveField}
				/>
			)}

			{patternDraft !== null && (
				<PatternDialog
					termId={term.id}
					draft={patternDraft}
					patterns={patterns}
					onClose={() => setPatternDraft(null)}
					onSaved={insertPattern}
				/>
			)}
		</div>
	);
}
